import type { MatchEvent } from "../../../../store/useMatchLogStore";
import { sameTeamId } from "../../utils/teamId";
import type {
  RecoveryOptions,
  RecoveryResult,
  TeamRecovery,
} from "./momentumTypes";

// On-ball event types that signal which team currently holds possession.
const POSSESSION_TYPES = new Set([
  "Pass",
  "Carry",
  "Shot",
  "Header",
  "Recovery",
  "Interception",
  "Clearance",
  "Block",
  "Duel",
  "GoalkeeperAction",
  "SetPiece",
]);

interface Spell {
  side: "home" | "away";
  seconds: number;
  period: number;
}

function summarize(
  spells: Spell[],
  side: "home" | "away",
  minSamples: number,
): TeamRecovery {
  const mine = spells.filter((s) => s.side === side);
  const byPeriodMap = new Map<number, number[]>();
  for (const s of mine) {
    const arr = byPeriodMap.get(s.period) ?? [];
    arr.push(s.seconds);
    byPeriodMap.set(s.period, arr);
  }
  const byPeriod = [...byPeriodMap.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([period, secs]) => ({
      period,
      avgSeconds: secs.reduce((a, b) => a + b, 0) / secs.length,
    }));
  const avgSeconds =
    mine.length >= minSamples && mine.length > 0
      ? mine.reduce((a, b) => a + b.seconds, 0) / mine.length
      : null;
  return { avgSeconds, sampleCount: mine.length, byPeriod };
}

export function computeRecoveryTime(
  events: MatchEvent[],
  opts: RecoveryOptions,
): RecoveryResult {
  const { homeTeamId, awayTeamId } = opts;
  const maxSpell = opts.maxSpellSeconds ?? 120;
  const minSamples = opts.minSamples ?? 3;

  const onBall = events
    .filter(
      (e) =>
        POSSESSION_TYPES.has(e.type) &&
        (sameTeamId(e.team_id, homeTeamId) ||
          sameTeamId(e.team_id, awayTeamId)),
    )
    .map((e) => ({
      side: (sameTeamId(e.team_id, homeTeamId) ? "home" : "away") as
        | "home"
        | "away",
      ts: Date.parse(e.timestamp),
      period: e.period,
    }))
    .filter((e) => !Number.isNaN(e.ts))
    .sort((a, b) => a.ts - b.ts);

  const spells: Spell[] = [];
  let owner: "home" | "away" | null = null;
  const lostAt: Record<"home" | "away", number | null> = {
    home: null,
    away: null,
  };

  for (const e of onBall) {
    if (owner === null) {
      owner = e.side;
      continue;
    }
    if (e.side !== owner) {
      // possession switched: `owner` lost the ball now; `e.side` regained it.
      const lost = lostAt[e.side];
      if (lost !== null) {
        const seconds = (e.ts - lost) / 1000;
        if (seconds > 0 && seconds <= maxSpell) {
          spells.push({ side: e.side, seconds, period: e.period });
        }
      }
      lostAt[owner] = e.ts;
      owner = e.side;
    }
  }

  return {
    home: summarize(spells, "home", minSamples),
    away: summarize(spells, "away", minSamples),
  };
}
